import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, LinearProgress, Chip, Tooltip, Alert } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useAuth } from '../context/AuthContext';

const arborGreen = '#006D43';

const QuotaBadge = ({ featureKey = 'ai_document_scan', onQuotaLoaded, compact = false }) => {
    const { t } = useTranslation();
    const { api } = useAuth();
    const [quota, setQuota] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchQuota = useCallback(async () => {
        try {
            const response = await api.get('/users/me/quota');
            const items = response.data || [];
            const q = items.find(item => item.feature_key === featureKey);
            if (q) {
                setQuota(q);
                if (onQuotaLoaded) onQuotaLoaded(q);
            }
        } catch (err) {
            console.error("Failed to fetch quota", err);
        } finally {
            setLoading(false);
        }
    }, [api, featureKey, onQuotaLoaded]);

    useEffect(() => {
        fetchQuota();
        const handleQuotaUpdate = () => {
            fetchQuota();
        };
        window.addEventListener('quota-updated', handleQuotaUpdate);
        return () => {
            window.removeEventListener('quota-updated', handleQuotaUpdate);
        };
    }, [fetchQuota]);


    if (loading || !quota) return null;

    const isUnlimited = quota.limit < 0;
    const percentage = isUnlimited ? 0 : Math.min(100, Math.round((quota.used / quota.limit) * 100));
    const isAdvice = featureKey === 'ai_advice';
    const unitLabel = isAdvice ? t('Insights') : t('Scans');
    const quotaTitle = isAdvice ? t('AI Advice Quota') : t('AI Scan Quota');

    if (compact) {
        return (
            <Chip
                icon={<AutoAwesomeIcon sx={{ fontSize: '1rem !important' }} />}
                label={isUnlimited ? `${t('Unlimited')} ${unitLabel}` : `${quota.used}/${quota.limit} ${unitLabel}`}
                color={!quota.is_allowed ? "error" : "default"}
                size="small"
                sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    bgcolor: !quota.is_allowed ? 'error.light' : 'rgba(0, 109, 67, 0.08)',
                    color: !quota.is_allowed ? 'error.dark' : arborGreen,
                }}
            />
        );
    }

    return (
        <Box sx={{ width: '100%', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoAwesomeIcon sx={{ color: arborGreen, fontSize: 18 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: arborGreen }}>
                        {quotaTitle}
                    </Typography>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {isUnlimited
                        ? t("Unlimited")
                        : `${quota.remaining} / ${quota.limit} ${t('remaining')} (${quota.reset_policy === 'MONTHLY' ? t('Monthly') : t('Lifetime')})`
                    }
                </Typography>
            </Box>


            {!isUnlimited && (
                <Tooltip title={`${quota.used} ${t('used of')} ${quota.limit} ${t('allowed')}`} arrow>
                    <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: 'rgba(0, 109, 67, 0.1)',
                            '& .MuiLinearProgress-bar': {
                                bgcolor: percentage >= 90 ? 'error.main' : arborGreen,
                                borderRadius: 3
                            }
                        }}
                    />
                </Tooltip>
            )}

            {!quota.is_allowed && (
                <Alert severity="warning" sx={{ mt: 1.5, py: 0.5, borderRadius: 2 }}>
                    {quota.reset_policy === 'MONTHLY'
                        ? t('Quota exceeded for this month. Please contact support or upgrade.')
                        : t('Quota exceeded for your Free plan. Please contact support or upgrade.')
                    }
                </Alert>
            )}
        </Box>
    );
};

export default QuotaBadge;
